import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clinicApi } from '../services/clinicApi';
import { contactApi } from '../services/contactApi';
import type { Contact, CreateContactRequest, UpdateContactRequest } from '../types/contact';

interface UseClinicContactsResult {
  contacts: Contact[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  createContact: (request: CreateContactRequest) => void;
  updateContact: (id: number, request: UpdateContactRequest) => void;
  deleteContact: (id: number) => void;
  isSaving: boolean;
  isDeleting: boolean;
}

/**
 * Encapsulates fetching, creating, updating, and deleting contacts for a clinic.
 * Used by ClinicContactsView so it can focus on presentation.
 */
export function useClinicContacts(
  clinicId: number,
  callbacks?: {
    onCreateSuccess?: () => void;
    onUpdateSuccess?: () => void;
    onDeleteSuccess?: () => void;
  },
): UseClinicContactsResult {
  const queryClient = useQueryClient();
  const queryKey = ['clinicContacts', clinicId];

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey });
  };

  const { data: contacts = [], isLoading, error } = useQuery({
    queryKey,
    queryFn: () => clinicApi.getClinicContacts(clinicId),
    enabled: !!clinicId,
  });

  const createMutation = useMutation({
    mutationFn: (request: CreateContactRequest) =>
      contactApi.createContact(clinicId, request),
    onSuccess: () => {
      invalidate();
      callbacks?.onCreateSuccess?.();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, request }: { id: number; request: UpdateContactRequest }) =>
      contactApi.updateContact(id, request),
    onSuccess: () => {
      invalidate();
      callbacks?.onUpdateSuccess?.();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => contactApi.deleteContact(id),
    onSuccess: () => {
      invalidate();
      callbacks?.onDeleteSuccess?.();
    },
  });

  return {
    contacts,
    isLoading,
    error: error as Error | null,
    refetch: invalidate,
    createContact: (request) => createMutation.mutate(request),
    updateContact: (id, request) => updateMutation.mutate({ id, request }),
    deleteContact: (id) => deleteMutation.mutate(id),
    isSaving: createMutation.isPending || updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
